import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { isAdminUser } from './adminConfig';

const defaultValue = { role: 'employee', isFullAccess: false, employeeId: null, loading: true };
const RoleContext = createContext(defaultValue);

export const useRole = () => useContext(RoleContext);

// Three tiers:
// - 'superadmin' -- the hardcoded ADMIN_EMAIL, or anyone assigned role:'superadmin' in
//   userRoles/{email} -- full access to everything, including HR.
// - 'administrator' -- assigned role:'administrator' -- everything an employee already
//   has, but explicitly NOT HR (Sohan: "Administrators can't see the HR option").
// - 'employee' -- the default when a login isn't listed in userRoles at all
//   (least-privilege default rather than trusting an unassigned login).
export const RoleProvider = ({ children }) => {
  const [state, setState] = useState(defaultValue);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setState({ role: 'employee', isFullAccess: false, employeeId: null, loading: false });
      return;
    }
    const hardcodedSuperadmin = isAdminUser(user);
    const emailKey = (user.email || '').toLowerCase();
    getDoc(doc(db, 'userRoles', emailKey))
      .then((snap) => {
        const data = snap.exists() ? snap.data() : {};
        // 'admin' is the old role value (before Sohan split it into superadmin/administrator
        // tiers) -- treat it as 'superadmin' so anyone already assigned it doesn't silently
        // lose access.
        const role = hardcodedSuperadmin || data.role === 'superadmin' || data.role === 'admin'
          ? 'superadmin'
          : data.role === 'administrator' ? 'administrator' : 'employee';
        setState({ role, isFullAccess: role === 'superadmin', employeeId: data.employeeId || null, loading: false });
      })
      .catch(() => {
        setState({ role: hardcodedSuperadmin ? 'superadmin' : 'employee', isFullAccess: hardcodedSuperadmin, employeeId: null, loading: false });
      });
  }, []);

  return <RoleContext.Provider value={state}>{children}</RoleContext.Provider>;
};
